import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import {
  useGetAllTypes,
  useToggleTypeStatusMutation,
} from "../../../../../api/jeep-rally/types/types.hooks";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import {
  filterTypes,
  getTypeApiErrorMessage,
  normalizeTypes,
} from "../utils/type.utils";
import TypesHeader from "./_components/TypesHeader";
import TypesTable from "./_components/TypesTable";

const TypesListPage = () => {
  const history = useHistory();
  const canManage = isJeepRallyAdmin();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [togglingId, setTogglingId] = useState(null);

  const { data, isLoading, isError, error } = useGetAllTypes({
    status: statusFilter,
  });
  const {
    mutate: toggleStatus,
    isLoading: isToggling,
    isPending: isTogglePending,
  } = useToggleTypeStatusMutation();

  const types = useMemo(() => normalizeTypes(data), [data]);
  const filteredTypes = useMemo(
    () => filterTypes(types, searchTerm),
    [types, searchTerm]
  );

  const toggling = isToggling || isTogglePending;

  const handleView = (type) => {
    const typeId = type?._id || type?.id;
    if (!typeId) return;
    history.push(`/jeep-rally/types/view/${typeId}`, { type });
  };

  const handleEdit = (type) => {
    const typeId = type?._id || type?.id;
    if (!typeId) return;
    history.push(`/jeep-rally/types/edit/${typeId}`, { type });
  };

  const handleToggleStatus = (type) => {
    const typeId = type?._id || type?.id;
    if (!typeId || !canManage) return;

    setFeedback({ type: "", message: "" });
    setTogglingId(typeId);
    toggleStatus(typeId, {
      onSuccess: (updated) => {
        const nextStatus = updated?.status || (type.status === "active" ? "inactive" : "active");
        setFeedback({
          type: "success",
          message: `Type status set to ${nextStatus}`,
        });
        setTogglingId(null);
      },
      onError: (toggleError) => {
        setFeedback({
          type: "danger",
          message: getTypeApiErrorMessage(
            toggleError,
            "Failed to update type status."
          ),
        });
        setTogglingId(null);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load types.{" "}
            {getTypeApiErrorMessage(error, "Please try again.")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <TypesHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          total={filteredTypes.length}
          canManage={canManage}
          onAddType={() => history.push("/jeep-rally/types/create")}
        />
        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredTypes.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No types found</p>
            </div>
          ) : (
            <TypesTable
              types={filteredTypes}
              onView={handleView}
              onEdit={handleEdit}
              onToggleStatus={handleToggleStatus}
              isToggling={toggling}
              togglingId={togglingId}
              canManage={canManage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TypesListPage;
